import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function HomePage() {
  const [showNotification, setShowNotification] = useState(true);
  const [showGuide, setShowGuide] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    direccion: '',
    servicio: '',
    descripcion: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para la posición de la ventana flotante
  const [guidePosition, setGuidePosition] = useState({ x: 24, y: 24 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Estado para el logo arrastrable
  const [logoPosition, setLogoPosition] = useState({ x: 20, y: 20 });
  const [isLogoDragging, setIsLogoDragging] = useState(false);
  const [logoDragOffset, setLogoDragOffset] = useState({ x: 0, y: 0 });

  // Handle scroll for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Funciones para arrastrar la ventana flotante
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      const maxX = window.innerWidth - 400;
      const maxY = window.innerHeight - 200;

      setGuidePosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Funciones para arrastrar el logo
  const handleLogoMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsLogoDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setLogoDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  useEffect(() => {
    const handleLogoMouseMove = (e: MouseEvent) => {
      if (!isLogoDragging) return;

      const newX = e.clientX - logoDragOffset.x;
      const newY = e.clientY - logoDragOffset.y;

      const maxX = window.innerWidth - 200;
      const maxY = window.innerHeight - 80;

      setLogoPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      });
    };

    const handleLogoMouseUp = () => {
      setIsLogoDragging(false);
    };

    if (isLogoDragging) {
      document.addEventListener('mousemove', handleLogoMouseMove);
      document.addEventListener('mouseup', handleLogoMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleLogoMouseMove);
      document.removeEventListener('mouseup', handleLogoMouseUp);
    };
  }, [isLogoDragging, logoDragOffset]);

  // Handle form submit
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('nombre', formData.nombre);
      formDataToSend.append('apellido', formData.apellido);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telefono', formData.telefono);
      formDataToSend.append('direccion', formData.direccion);
      formDataToSend.append('servicio', formData.servicio);
      formDataToSend.append('descripcion', formData.descripcion);

      const response = await fetch('https://readdy.ai/api/form/d4ulns6cd4084g36bu1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          direccion: '',
          servicio: '',
          descripcion: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animaciones de scroll
  const heroAnimation = useScrollAnimation({ threshold: 0.2 });
  const infoAnimation = useScrollAnimation({ threshold: 0.2 });
  const specialPhotoAnimation = useScrollAnimation({ threshold: 0.3 });
  const formAnimation = useScrollAnimation({ threshold: 0.2 });

  return (
    <div className="min-h-screen bg-white">
      {/* Logo Arrastrable Flotante */}
      <div 
        className="fixed z-[9999] transition-shadow duration-300"
        style={{
          left: `${logoPosition.x}px`,
          top: `${logoPosition.y}px`,
          cursor: isLogoDragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        onMouseDown={handleLogoMouseDown}
      >
        <div className={`relative bg-white rounded-2xl shadow-2xl p-4 transition-all duration-300 ${
          isLogoDragging ? 'scale-110 shadow-3xl' : 'hover:shadow-3xl hover:scale-105'
        }`}>
          {/* Indicador de arrastre */}
          <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-teal-600/40 rounded-full"></div>
          
          {/* Efecto de brillo */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-teal-400/20 to-transparent rounded-2xl animate-shimmer pointer-events-none"></div>
          
          {/* Logo */}
          <img 
            src="https://static.readdy.ai/image/e5ec1a966ead4ea4405a383d967bb80c/5b74a69037f50dd7137b29dc6f7b5981.png"
            alt="HogarSoluciones Ecuador Logo"
            className="h-16 w-auto pointer-events-none"
            draggable="false"
          />
          
          {/* Texto de ayuda */}
          {!isLogoDragging && (
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-teal-600 text-white text-xs px-3 py-1 rounded-full whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              ¡Arrástrame! 🎯
            </div>
          )}
          
          {/* Destellos decorativos */}
          <div className="absolute -top-2 -right-2 text-yellow-400 text-xl animate-pulse pointer-events-none">✨</div>
          <div className="absolute -bottom-2 -left-2 text-teal-400 text-lg animate-pulse pointer-events-none" style={{ animationDelay: '0.5s' }}>💫</div>
        </div>
      </div>

      {/* Guía Flotante de Comunicación - ARRASTRABLE */}
      {showGuide && (
        <div 
          className="fixed z-50 max-w-md animate-fade-in-up"
          style={{
            left: `${guidePosition.x}px`,
            bottom: `${guidePosition.y}px`,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none'
          }}
          onMouseDown={handleMouseDown}
        >
          <div className="bg-gradient-to-br from-teal-600 to-cyan-600 rounded-2xl shadow-2xl p-6 text-white relative overflow-hidden">
            {/* Decoración de fondo */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
            
            {/* Indicador de arrastre */}
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-white/40 rounded-full"></div>
            
            {/* Botón cerrar */}
            <button
              onClick={() => setShowGuide(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-all cursor-pointer z-10"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {/* Contenido */}
            <div className="relative">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                  <i className="ri-information-line text-2xl text-teal-700"></i>
                </div>
                <h3 className="text-xl font-bold">Guía de Comunicación Efectiva</h3>
              </div>

              <p className="text-sm mb-4 text-white/90 font-medium">
                Para brindarte el mejor servicio, sigue estas recomendaciones:
              </p>

              <div className="space-y-3">
                <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-700 font-bold text-sm">1</span>
                  </div>
                  <p className="text-sm leading-relaxed">
                    <strong>Economice palabras</strong> antes de usar la opción de voz
                  </p>
                </div>

                <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-700 font-bold text-sm">2</span>
                  </div>
                  <p className="text-sm leading-relaxed">
                    <strong>Planifique sus preguntas</strong> para evitar saturar el canal
                  </p>
                </div>

                <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-700 font-bold text-sm">3</span>
                  </div>
                  <p className="text-sm leading-relaxed">
                    <strong>Prefiera el chat de texto:</strong> permite leer con exactitud, releer y observar detalles importantes
                  </p>
                </div>

                <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-700 font-bold text-sm">4</span>
                  </div>
                  <p className="text-sm leading-relaxed">
                    <strong>Sea preciso en los detalles físicos</strong> para evaluaciones exactas y respuestas acertadas
                  </p>
                </div>

                <div className="flex items-start space-x-3 bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-teal-700 font-bold text-sm">5</span>
                  </div>
                  <p className="text-sm leading-relaxed">
                    <strong>Use la opción de voz</strong> después de aprovechar el chat de texto para mayor claridad
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-xs text-center text-white/80 italic">
                  💡 Estas prácticas nos ayudan a servirle mejor y más rápido
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notificación superior */}
      {showNotification && (
        <div className="fixed inset-x-0 top-0 bg-amber-100 text-amber-900 py-2 px-4 flex items-center justify-between z-50">
          <p className="text-sm font-medium">
            <strong>¡ATENCIÓN!</strong> Estamos 24/7, respuestas instantáneas vía chat y llamadas.
          </p>
          <button
            onClick={() => setShowNotification(false)}
            className="text-amber-900 hover:text-amber-700 cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      {/* Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3 cursor-pointer opacity-0 pointer-events-none">
              <img 
                src="https://static.readdy.ai/image/e5ec1a966ead4ea4405a383d967bb80c/5b74a69037f50dd7137b29dc6f7b5981.png"
                alt="HogarSoluciones Ecuador Logo"
                className="h-16 w-auto"
              />
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-teal-600' : 'text-white'}`}>
                Inicio
              </Link>
              <Link to="/servicios" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Servicios
              </Link>
              <Link to="/blog" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Blog
              </Link>
              <Link to="/contacto" className={`text-lg font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>
                Únete como Técnico
              </Link>
              <a href="#solicitar" className="bg-teal-600 text-white px-6 py-3 rounded-lg text-base font-semibold hover:bg-teal-700 whitespace-nowrap cursor-pointer">
                Solicitar Servicio
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        ref={heroAnimation.elementRef}
        className={`relative bg-cover bg-center py-32 lg:py-40 transition-all duration-1000 ${
          heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
        style={{
          backgroundImage: `linear-gradient(rgba(20, 184, 166, 0.85), rgba(20, 184, 166, 0.75)), url('https://public.readdy.ai/ai/img_res/eb9727839f7da11a32edd9278250e5b9.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full text-center">
            <h2 className={`text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight transition-all duration-1000 delay-200 ${
              heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              Técnicos especializados<br />
              <span className="text-yellow-300">a su servicio</span>
            </h2>
            <p className={`text-xl lg:text-2xl text-white/95 mb-6 max-w-4xl mx-auto leading-relaxed transition-all duration-1000 delay-400 ${
              heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              <span className="text-black font-bold bg-white/95 px-4 py-2 rounded-lg inline-block">
                Encuentre profesionales confiables para remodelaciones y reparaciones: albañilería, plomería, cerrajería, pintura, electricidad, refrigeración, aire acondicionado y jardinería. Conéctese con expertos de confianza que simplifican las reparaciones del hogar, oficina y locales comerciales.
              </span>
            </p>
            <p className={`text-2xl lg:text-4xl text-yellow-300 font-extrabold mb-10 max-w-3xl mx-auto transition-all duration-1000 delay-600 bg-black/30 px-6 py-4 rounded-xl shadow-2xl ${
              heroAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              También le servimos en cualquier ciudad y pueblo pequeño de nuestro país, Ecuador. <span className="text-3xl lg:text-5xl font-black text-white drop-shadow-lg">¡Anticípanos el comienzo pautado de la obra requerida!</span>
            </p>
          </div>
        </div>
      </section>

      {/* Información rápida */}
      <section ref={infoAnimation.elementRef} className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 px-4">
          <div className="bg-white p-6 rounded-lg shadow-sm text-center hover:shadow-lg transition-shadow duration-300">
            <i className="ri-phone-line text-4xl text-teal-600 mb-4"></i>
            <h3 className="font-semibold text-lg mb-2">Llamada Directa</h3>
            <p className="text-gray-600">Responde al instante, sin esperas</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm text-center hover:shadow-lg transition-shadow duration-300">
            <i className="ri-chat-3-line text-4xl text-teal-600 mb-4"></i>
            <h3 className="font-semibold text-lg mb-2">Chat 24/7</h3>
            <p className="text-gray-600">Asesoría instantánea vía mensajería</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm text-center hover:shadow-lg transition-shadow duration-300">
            <i className="ri-timer-line text-4xl text-teal-600 mb-4"></i>
            <h3 className="font-semibold text-lg mb-2">Respuesta Rápida</h3>
            <p className="text-gray-600">Atención en menos de 5 minutos</p>
          </div>
        </div>
      </section>

      {/* Foto Especial - Técnico y Dama Intercambiando Sonrisas */}
      <section ref={specialPhotoAnimation.elementRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-1000 ${
            specialPhotoAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              Satisfacción Garantizada
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Nuestro compromiso es conectar a los mejores técnicos con clientes que valoran la calidad
            </p>
          </div>

          <div className={`max-w-4xl mx-auto transition-all duration-1000 delay-200 ${
            specialPhotoAnimation.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="relative group">
              {/* Marco decorativo exterior */}
              <div className="absolute -inset-6 bg-gradient-to-r from-teal-600 via-cyan-500 to-teal-600 rounded-3xl opacity-75 group-hover:opacity-100 blur-2xl group-hover:blur-3xl transition-all duration-700 animate-pulse-slow"></div>
              
              {/* Contenedor principal */}
              <div className="relative bg-white rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-700 transform group-hover:scale-105">
                {/* Marco interior dorado */}
                <div className="relative border-8 border-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=elderly%20experienced%20male%20technician%20in%20professional%20work%20uniform%20standing%20on%20left%20side%20and%20elderly%20female%20homeowner%20standing%20on%20right%20side%20both%20looking%20at%20each%20other%20from%20distance%20with%20warm%20genuine%20smiles%20happy%20satisfied%20expressions%20after%20successful%20home%20repair%20service%20completion%20bright%20natural%20daylight%20warm%20atmosphere%20professional%20photography%20high%20quality&width=1200&height=600&seq=tech-client-distance-smiles-special-001&orientation=landscape"
                    alt="Técnico y cliente satisfechos intercambiando sonrisas"
                    className="w-full h-96 object-cover object-center group-hover:scale-110 transition-transform duration-1000"
                  />
                  {/* Overlay con degradado */}
                  <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  
                  {/* Banner inferior */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-teal-600/95 to-transparent p-6">
                    <p className="text-white text-center font-bold text-2xl animate-fade-in-up drop-shadow-lg">
                      ✨ La Satisfacción de Nuestros Clientes es Nuestra Mayor Recompensa ✨
                    </p>
                  </div>
                  
                  {/* Efecto de brillo al hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 animate-shimmer"></div>
                </div>
                
                {/* Decoración de esquinas grandes */}
                <div className="absolute top-4 left-4 w-16 h-16 border-t-8 border-l-8 border-yellow-400 rounded-tl-2xl"></div>
                <div className="absolute top-4 right-4 w-16 h-16 border-t-8 border-r-8 border-yellow-400 rounded-tr-2xl"></div>
                <div className="absolute bottom-4 left-4 w-16 h-16 border-b-8 border-l-8 border-yellow-400 rounded-bl-2xl"></div>
                <div className="absolute bottom-4 right-4 w-16 h-16 border-b-8 border-r-8 border-yellow-400 rounded-br-2xl"></div>
              </div>
            </div>

            {/* Texto descriptivo debajo */}
            <div className="mt-8 text-center">
              <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                Cada sonrisa de satisfacción de nuestros clientes nos motiva a seguir conectando a los mejores profesionales con quienes necesitan servicios de calidad en todo Ecuador.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de solicitud */}
      <section id="solicitar" ref={formAnimation.elementRef} className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-teal-700 mb-8">
            Solicita tu Servicio
          </h2>

          <form id="solicitud-servicio" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 grid gap-6">
            <div className="grid md:grid-cols-2 gap-4">
              <input
                name="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required
                placeholder="Nombre"
                className="border border-gray-300 rounded px-4 py-2"
              />
              <input
                name="apellido"
                value={formData.apellido}
                onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                required
                placeholder="Apellido"
                className="border border-gray-300 rounded px-4 py-2"
              />
            </div>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="Correo Electrónico"
              className="border border-gray-300 rounded px-4 py-2 w-full"
            />

            <div className="grid md:grid-cols-2 gap-4">
              <input
                name="telefono"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                required
                placeholder="Teléfono"
                className="border border-gray-300 rounded px-4 py-2"
              />
              <input
                name="direccion"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                required
                placeholder="Dirección"
                className="border border-gray-300 rounded px-4 py-2"
              />
            </div>

            <select
              name="servicio"
              value={formData.servicio}
              onChange={(e) => setFormData({ ...formData, servicio: e.target.value })}
              required
              className="border border-gray-300 rounded px-4 py-2 w-full cursor-pointer"
            >
              <option value="">Selecciona un servicio</option>
              <option value="albanileria">Albañilería</option>
              <option value="plomeria">Plomería</option>
              <option value="electricidad">Electricidad</option>
              <option value="pintura">Pintura</option>
              <option value="cerrajeria">Cerrajería</option>
              <option value="refrigeracion">Refrigeración y Aire Acondicionado</option>
              <option value="jardineria">Jardinería</option>
              <option value="reparaciones">Reparaciones Generales</option>
            </select>

            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              required
              placeholder="Describe tu necesidad"
              rows={4}
              maxLength={500}
              className="border border-gray-300 rounded px-4 py-2 w-full resize-none"
            ></textarea>
            <p className="text-xs text-gray-500 -mt-4">Máximo 500 caracteres</p>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-teal-600 text-white font-semibold py-3 px-6 rounded-md hover:bg-teal-700 transition whitespace-nowrap cursor-pointer ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
            </button>

            {submitStatus === 'success' && (
              <p className="text-green-600 mt-4 text-center">
                ¡Solicitud enviada con éxito! Nos pondremos en contacto pronto.
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-600 mt-4 text-center">
                Ocurrió un error. Por favor, intenta nuevamente.
              </p>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <i className="ri-home-heart-fill text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold" style={{fontFamily: "Pacifico, serif"}}>
                  HogarSoluciones
                </h3>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Técnicos especializados a su servicio, soluciones a su alcance. Conectando hogares, oficinas y locales comerciales con profesionales en todo Ecuador.
              </p>
              
              {/* Redes Sociales */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold mb-4">Síguenos en Redes Sociales</h4>
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="https://www.facebook.com/profile.php?id=61571046803494" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-facebook-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Facebook</p>
                  </a>
                  
                  <a 
                    href="https://www.instagram.com/hogarsolucionesecuador/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-instagram-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Instagram</p>
                  </a>
                  
                  <a 
                    href="https://wa.me/593978966415" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-whatsapp-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">WhatsApp</p>
                  </a>
                  
                  <a 
                    href="https://www.tiktok.com/@hogarsolucionesecuador" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg border-2 border-teal-400">
                      <i className="ri-tiktok-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">TikTok</p>
                  </a>
                  
                  <a 
                    href="mailto:info@hogarsolucionesecuador.com"
                    className="group"
                  >
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg">
                      <i className="ri-mail-fill text-3xl text-white"></i>
                    </div>
                    <p className="text-xs text-center mt-2 text-gray-400 group-hover:text-white transition-colors">Email</p>
                  </a>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Servicios</h4>
              <ul className="space-y-2">
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Albañilería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Plomería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Cerrajería</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Pintura</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Electricidad</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Refrigeración y A/C</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Jardinería</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white cursor-pointer">Inicio</Link></li>
                <li><Link to="/servicios" className="text-gray-400 hover:text-white cursor-pointer">Servicios</Link></li>
                <li><Link to="/contacto" className="text-gray-400 hover:text-white cursor-pointer">Únete como Técnico</Link></li>
                <li><a href="#solicitar" className="text-gray-400 hover:text-white cursor-pointer">Solicitar Servicio</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contacto</h4>
              <ul className="space-y-2">
                <li className="text-gray-400">📧 info@hogarsolucionesecuador.com</li>
                <li>
                  <a href="tel:+593978966415" className="text-gray-400 hover:text-white cursor-pointer">
                    📱 +593 978 966 415 - HogarSoluciones
                  </a>
                </li>
                <li>
                  <a href="tel:+584248082841" className="text-gray-400 hover:text-white cursor-pointer">
                    📱 +58 424 808 2841 - MILVA
                  </a>
                </li>
                <li className="text-gray-400">📍 CALLES JULIA MOSQUERA CON PEDRO PÉREZ, CIUDADELA YANAYACU 1RA ETAPA, OTAVALO</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2024 HogarSoluciones Ecuador. Todos los derechos reservados.
            </p>
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}